import Vue from 'vue';
import { mapState } from 'vuex';
import _isNil from 'lodash/isNil';
import {
	BalanceAccount,
	calcEnterpriseTokenUsage,
	currencyTokens,
	EnterpriseTokenUsage,
	hundredthsToTokens,
	Team,
} from '@run-diffusion/shared';
import {
	deriveEnterpriseRing,
	deriveRingFill,
	deriveTokenBuckets,
	deriveTokenStatus,
	EnterpriseRing,
	TOKEN_STATUS_HERO_COLOR,
	TokenBuckets,
	TokenStatus,
} from '@/utils/tokenBuckets';
import { MyBalanceMixin } from './MyBalanceMixin';

/**
 * Everything needed to render an account's token balance the way the sidebar card does: the
 * headline number, the colour it takes, and the consumption ring's fill and status.
 *
 * It lives here rather than in the card because two surfaces show the same balance — the sidebar
 * account card and the token chip beside the Run button — and the arithmetic underneath is not
 * obvious enough to be worth having twice. Three account shapes disagree about what "how many
 * tokens" even means: a consumer drains a purchased balance, a freemium account refills a daily
 * allowance nightly, and an enterprise team spends against a budget frozen at renewal.
 *
 * The computeds read from the ACTIVE account (MyBalanceMixin's runnitsOwnerSelection), so the ring
 * always describes the number printed next to it. The enterprise pieces are also exposed as
 * methods taking an explicit team and balance doc, for a surface that shows a team OTHER than the
 * active one (the company teams table's chip, and the dialog it opens). The arithmetic itself lives
 * in shared (calcEnterpriseTokenUsage), which the team dashboard reads too, so every surface says
 * the same "N used".
 */
export const TokenRingMixin = Vue.extend({
	mixins: [
		MyBalanceMixin,
	],
	computed: {
		...mapState([
			'user',
			'team',
			'userBalanceAccount',
			'teamBalanceAccount',
			'loadingUserBalanceAccount',
			'loadingTeamBalanceAccount',
		]),
		isEnterprise (): boolean {
			return !!(!this.onPersonalPlan && this.isEnterpriseTeam(this.team));
		},
		isFreemium (): boolean {
			return !!(this.onPersonalPlan && this._get(this.user, 'clubOffer.isFreemium'));
		},
		/*
		A team that is not active has no plan: no cycle, no allotment, nothing to buy. Wherever this
		account's tokens are shown, that state is shown instead of a balance — there is no "free" team plan
		for it to fall back to, whatever club its company happens to sit on.

		isActive and activatedAt are only ever written together (toggleDeactivateCompanyTeams,
		onStripeTeamsCustomerSubscriptionWrite, setupNewTeam), so isActive alone decides it. The team doc
		has to be present: one still binding is not a deactivated one, and must not flash as one.
		 */
		isDeactivatedTeam (): boolean {
			return !!(!this.onPersonalPlan && this.team && !this.team.isActive);
		},
		/*
		Every number here is derived from the balance account, so before it lands a consumer would
		otherwise render a fabricated "0" — a real balance and an unloaded store are indistinguishable
		once you are downstream of the arithmetic. null = binding not started, true = in flight.
		 */
		balanceLoading (): boolean {
			const loading = this.onPersonalPlan ? this.loadingUserBalanceAccount : this.loadingTeamBalanceAccount;
			return _isNil(loading) || !!loading || _isNil(this.activeBalanceAccount);
		},
		buckets (): TokenBuckets {
			return deriveTokenBuckets(this.activeBalanceAccount);
		},
		/*
		The nightly free allowance, or 0 when this account has none. calcRunnitTokensDataForBalanceAccount
		forces freeRunnitTokens to 0 without a valid freemium passport, so a Free-plan user without one
		refills nothing — measuring their ring against a made-up cap would be fiction.
		 */
		dailyCap (): number {
			const data = this.userBalanceAccountRunnitTokensData;
			if (!this._get(data, 'isFreemiumPassportValid')) return 0;
			return this._get(data, 'dailyFreeRunnitTokensAllotmentIfValid') || 0;
		},
		freeTokensHeld (): number {
			return Math.max(this._get(this.userBalanceAccountRunnitTokensData, 'freeRunnitTokens') || 0, 0);
		},
		/*
		What the account can actually spend: free daily tokens PLUS the paid balance. A Free-plan user
		can hold paid tokens (bought, granted, or left over from a lapsed plan), and showing only the
		free bucket hides them — which is what made a 100-token account read as 0.
		 */
		spendableTokens (): number {
			return this.freeTokensHeld + this.buckets.total;
		},
		isOnDailyFallback (): boolean {
			return !!(
				this.onPersonalPlan &&
				!this.isFreemium &&
				this._get(this.userBalanceAccountRunnitTokensData, 'combinedTokens', 0) <= this._get(this.userBalanceAccountRunnitTokensData, 'dailyFreeRunnitTokensAllotmentIfValid', 0)
			);
		},
		/*
		Living on the nightly free allowance: a Free-plan account, or a paid one that has spent down
		to (or below) its daily tokens. Both spend the same free bucket, so both have to count it in
		the number and measure against the daily cap rather than a monthly allotment. The dialog
		treats exactly this pair alike, so keeping one definition is what stops the card and the
		dialog disagreeing (a paid account on the fallback read its paid bucket alone as "12" on the
		card while the dialog said "100"; a Free account at 12 of 100 went yellow on the card while
		the dialog, judging 0 paid against no allotment, stayed white).
		 */
		onDailyAllowance (): boolean {
			return !!(this.isFreemium || this.isOnDailyFallback);
		},
		cycleAllotment (): number {
			if (this.isFreemium) return this.dailyCap;
			if (this.onPersonalPlan) return this._get(this.user, 'clubInfo.token.tokens') || 0;
			return this._get(this.team, 'allotmentSettings.monthlyRunnitTokensBudget') || 0;
		},
		tokenNumberDisplay (): string {
			if (this.isEnterprise) return this.enterpriseUsedDisplay(this.team, this.teamBalanceAccount);
			if (this.onDailyAllowance) return currencyTokens(hundredthsToTokens(this.spendableTokens));
			return currencyTokens(hundredthsToTokens(this.buckets.total));
		},
		// Enterprise counts UP against its budget, so its number needs saying which way it runs
		tokenNumberSuffix (): string {
			return this.isEnterprise ? 'used' : '';
		},
		// What a consumer account's ring measures: the balance held against the cycle allotment. The
		// status keys its thresholds off the same numbers, so ok/low/depleted line up with the fill.
		// (An enterprise team's ring comes from enterpriseRing instead.)
		ringHeld (): number {
			if (this.onDailyAllowance) return this.spendableTokens;
			return this.buckets.total;
		},
		ringAllotment (): number {
			// No daily allowance -> nothing to measure against, so the ring reads "unknown" (empty, neutral)
			if (this.onDailyAllowance) return this.dailyCap > 0 ? Math.max(this.spendableTokens, this.dailyCap) : 0;
			return this.cycleAllotment;
		},
		ringStatus (): TokenStatus {
			// Never colour a status off data that has not arrived
			if (this.balanceLoading) return 'ok';
			if (this.isEnterprise) return this.enterpriseRing(this.team, this.teamBalanceAccount).status;
			/*
			Judged against the daily cap, the same allowance the number and the ring measure. A paid
			account that fell through to it still reads depleted whatever it holds today, which is the
			dialog's "USING DAILY TOKENS" treatment; a Free account reads low or depleted off its own
			cap, which no monthly allotment could express.
			 */
			if (this.onDailyAllowance) {
				return deriveTokenStatus({
					paidTokens: this.spendableTokens,
					cycleAllotment: this.dailyCap,
					isOnDailyFallback: this.isOnDailyFallback,
				});
			}
			// Consumer plans and non-enterprise Team Plans both drain a purchased balance
			return deriveTokenStatus({
				paidTokens: this.buckets.total,
				cycleAllotment: this.cycleAllotment,
				isOnDailyFallback: false,
			});
		},
		numberColor (): string {
			return TOKEN_STATUS_HERO_COLOR[this.ringStatus] || TOKEN_STATUS_HERO_COLOR.ok;
		},
		ringFill (): number {
			if (this.isEnterprise) return this.enterpriseRing(this.team, this.teamBalanceAccount).fill;
			return deriveRingFill({
				cycleAllotment: this.ringAllotment,
				held: this.ringHeld,
			});
		},
	},
	methods: {
		/*
		The enterprise pieces for ANY team and its balance doc. The computeds above apply them to the
		active account; a surface showing another team (the company teams table's chip, the dialog it
		opens for a row) calls them with that team, so it reads the same definition as the sidebar.
		 */
		isEnterpriseTeam (team: Team): boolean {
			return !!this._get(team, 'company.clubOffer.benefits.isEnterprise');
		},
		// Guarded on the team doc the way teamBalanceAccountRunnitTokensData is: no team, nothing used
		enterpriseUsage (team: Team, balanceAccount: BalanceAccount): EnterpriseTokenUsage {
			return calcEnterpriseTokenUsage(team ? balanceAccount : null, team);
		},
		enterpriseRing (team: Team, balanceAccount: BalanceAccount): EnterpriseRing {
			return deriveEnterpriseRing(this.enterpriseUsage(team, balanceAccount));
		},
		enterpriseUsedDisplay (team: Team, balanceAccount: BalanceAccount): string {
			return currencyTokens(hundredthsToTokens(this.enterpriseUsage(team, balanceAccount).used));
		},
	},
});
